import React from 'react';
import { motion, Reorder } from 'framer-motion';
import { FaPlus, FaTrash, FaGripVertical } from 'react-icons/fa';
import type { InvoiceItem } from '~/shared/types/invoice';

interface ItemsTabProps {
  items: InvoiceItem[];
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (id: string, field: string, value: string | number) => void;
  onReorderItems: (newOrder: InvoiceItem[]) => void;
  t?: Record<string, string>;
}

const ItemsTab: React.FC<ItemsTabProps> = ({
  items,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
  onReorderItems,
  t
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* رأس القسم */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white">
          {t?.title || 'الأصناف'}
        </h3>
        <button
          onClick={onAddItem}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FaPlus />
          {t?.addItem || 'إضافة صنف'}
        </button>
      </div>

      {/* قائمة الأصناف */}
      {items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">
            {t?.emptyTitle || 'لا توجد أصناف'}
          </h4>
          <p className="text-gray-500 dark:text-gray-500 mb-4">
            {t?.emptyDescription || 'ابدأ بإضافة الأصناف إلى الفاتورة'}
          </p>
          <button
            onClick={onAddItem}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus />
            {t?.addFirstItem || 'إضافة أول صنف'}
          </button>
        </div>
      ) : (
        <Reorder.Group
          axis="y"
          values={items}
          onReorder={onReorderItems}
          className="space-y-4"
        >
          {items.map((item) => (
            <Reorder.Item
              key={item.id}
              value={item}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <motion.div
                layout
                className="p-6"
              >
                <div className="flex items-start gap-4">
                  {/* مقبض السحب */}
                  <div className="flex-shrink-0 mt-2">
                    <FaGripVertical className="text-gray-400 cursor-grab active:cursor-grabbing" />
                  </div>

                  {/* محتوى الصنف */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t?.description || 'الوصف'}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => onUpdateItem(item.id, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={t?.descriptionPlaceholder || 'أدخل وصف الصنف'}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t?.quantity || 'الكمية'}
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => onUpdateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t?.price || 'السعر'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => onUpdateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* أزرار التحكم */}
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <button
                      onClick={() => onRemoveItem(item.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title={t?.removeItem || 'حذف الصنف'}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {/* ملاحظات الصنف */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t?.notes || 'ملاحظات'} ({t?.optional || 'اختياري'})
                  </label>
                  <textarea
                    value={item.notes || ''}
                    onChange={(e) => onUpdateItem(item.id, 'notes', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t?.notesPlaceholder || 'أدخل ملاحظات إضافية'}
                  />
                </div>

                {/* المجموع الفرعي */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t?.subtotal || 'المجموع الفرعي'}:
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-white">
                      {new Intl.NumberFormat('ar-SA', {
                        style: 'currency',
                        currency: 'SAR',
                        minimumFractionDigits: 2
                      }).format(item.quantity * item.price)}
                    </span>
                  </div>
                </div>
              </motion.div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      {/* ملخص الأصناف */}
      {items.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
            {t?.summary || 'ملخص الأصناف'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {items.length}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t?.totalItems || 'إجمالي الأصناف'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {items.reduce((sum, item) => sum + item.quantity, 0)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t?.totalQuantity || 'إجمالي الكمية'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {new Intl.NumberFormat('ar-SA', {
                  style: 'currency',
                  currency: 'SAR',
                  minimumFractionDigits: 2
                }).format(items.reduce((sum, item) => sum + (item.quantity * item.price), 0))}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t?.totalAmount || 'إجمالي المبلغ'}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ItemsTab; 